import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AuthServiceUAU, SessionState } from '../services/authServiceUAU';
import { ApiClient } from '../services/apiClient';
import type { User, LoginRequest, CambiarPasswordRequest } from '../types';
import toast from 'react-hot-toast';

// ===============================
// MAPEO UAU → MAVA COMPATIBLE
// ===============================

interface UAUUserData {
  user_id: number;
  username: string;
  email: string;
  full_name?: string;
  is_active: boolean;
  user_type: string;
  organizations: Array<{ id: number; name: string }>;
  permissions: Record<string, boolean>;
}

const mapUAUToMavaUser = (uauData: UAUUserData): User => {
  // Convertir permisos planos a estructura MAVA
  const permisos = Object.entries(uauData.permissions)
    .filter(([_, granted]) => granted)
    .map(([codigo, _]) => ({
      id: Math.random(), // ID temporal
      codigo,
      nombre: codigo,
      descripcion: `Permiso ${codigo}`,
      categoria: codigo.split('.')[0] || 'general'
    }));

  // Mapear organizaciones UAU → empresas MAVA
  const empresas = uauData.organizations.map(org => ({
    id: org.id,
    nombre: org.name,
    es_activa: true
  }));

  return {
    id: uauData.user_id,
    username: uauData.username,
    email: uauData.email,
    nombre_completo: uauData.full_name || uauData.username,
    es_activo: uauData.is_active,
    tipo_usuario: uauData.user_type as 'operador' | 'supervisor' | 'administrador',
    auth_service_id: uauData.user_id.toString(),
    permisos,
    maquinas_asignadas: [],
    empresas_asignadas: empresas,
    empresa_activa: empresas[0] || null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
};

// ===============================
// TIPOS COMPATIBLES CON CÓDIGO EXISTENTE
// ===============================

interface EstadoAuth {
  usuario: User | null;
  estaAutenticado: boolean;
  cargando: boolean;
  esAdmin: boolean;
  esSuperuser: boolean;
  permisos: string[];
  idsMAquinas: number[];
  estadoSesion: SessionState;
  reconectando: boolean;
  organizaciones: Array<{ id: number; name: string }>;
  empresaActiva: { id: number; nombre: string } | null;
}

interface AccionesAuth {
  iniciarSesion: (credenciales: LoginRequest) => Promise<void>;
  cerrarSesion: () => Promise<void>;
  actualizarPerfil: () => Promise<void>;
  editarPerfil: (data: Partial<User>) => Promise<void>;
  cambiarPassword: (data: CambiarPasswordRequest) => Promise<void>;
  tienePermiso: (permiso: string) => boolean;
  tieneAlgunPermiso: (permisos: string[]) => boolean;
  tieneTodosLosPermisos: (permisos: string[]) => boolean;
  tieneAccesoMaquina: (maquinaId: number) => boolean;
  verificarSesion: () => Promise<void>;
  manejarReconexion: () => Promise<void>;
  cambiarEmpresa: (empresaId: number) => Promise<void>;
  refrescarPermisos: () => Promise<void>;
}

export type AuthContextType = EstadoAuth & AccionesAuth & {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isSuperuser: boolean;
  permissions: string[];
  hasPermission: (permiso: string) => boolean;
  hasAnyPermission: (permisos: string[]) => boolean;
  hasAllPermissions: (permisos: string[]) => boolean;
  sessionState: SessionState;
  isReconnecting: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [estado, setEstado] = useState<EstadoAuth>({
    usuario: null,
    estaAutenticado: false,
    cargando: true,
    esAdmin: false,
    esSuperuser: false,
    permisos: [],
    idsMAquinas: [],
    estadoSesion: SessionState.CHECKING,
    reconectando: false,
    organizaciones: [],
    empresaActiva: null
  });

  useEffect(() => {
    let isMounted = true;

    const inicializarAuth = async () => {
      try {
        const infoUsuario = AuthServiceUAU.getCurrentUserInfo();

        if (infoUsuario.isAuthenticated && infoUsuario.user) {
          try {
            const userInfoData = await ApiClient.get<any>('/auth/user-info');

            const uauUserData: UAUUserData = {
              user_id: userInfoData.id,
              username: userInfoData.username,
              email: userInfoData.email,
              full_name: userInfoData.nombre_completo,
              is_active: userInfoData.es_activo,
              user_type: userInfoData.tipo_usuario,
              organizations: [],
              permissions: userInfoData.permisos_menu.reduce((acc: Record<string, boolean>, perm: string) => {
                acc[perm] = true;
                return acc;
              }, {})
            };

            const usuarioMapeado = mapUAUToMavaUser(uauUserData);
            usuarioMapeado.permisos_menu = userInfoData.permisos_menu;
            
            if (isMounted) {
              const permisosFinales = (usuarioMapeado.permisos || []).map((p: any) => p.codigo);
              setEstado({
                usuario: usuarioMapeado,
                estaAutenticado: true,
                cargando: false,
                esAdmin: usuarioMapeado.tipo_usuario === 'administrador',
                esSuperuser: userInfoData.is_superuser || false,
                permisos: permisosFinales,
                idsMAquinas: usuarioMapeado.maquinas_asignadas?.map(ma => ma.maquina_id) || [],
                estadoSesion: SessionState.VALID,
                reconectando: false,
                organizaciones: uauUserData.organizations,
                empresaActiva: usuarioMapeado.empresa_activa
              });
            }
            return;
          } catch (apiError) {
            // Fallback a datos del token si falla user-info
          }

          const usuarioFallback: User = {
            id: infoUsuario.user.id || 0,
            username: infoUsuario.user.username || '',
            email: infoUsuario.user.email || '',
            nombre_completo: infoUsuario.user.nombre_completo || '',
            es_activo: true,
            tipo_usuario: infoUsuario.isAdmin ? 'administrador' : 'operador',
            auth_service_id: (infoUsuario.user.id || 0).toString(),
            permisos: infoUsuario.permissions.map(p => ({
              id: Math.random(),
              codigo: p,
              nombre: p,
              descripcion: `Permiso ${p}`,
              categoria: p.split('.')[0] || 'general'
            })),
            maquinas_asignadas: [],
            empresas_asignadas: [],
            empresa_activa: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          if (isMounted) {
            setEstado({
              usuario: usuarioFallback,
              estaAutenticado: true,
              cargando: false,
              esAdmin: infoUsuario.isAdmin,
              esSuperuser: infoUsuario.isSuperuser || false,
              permisos: infoUsuario.permissions,
              idsMAquinas: infoUsuario.machineIds,
              estadoSesion: SessionState.VALID,
              reconectando: false,
              organizaciones: [],
              empresaActiva: null
            });
          }
        } else {
          if (isMounted) {
            setEstado(prev => ({
              ...prev,
              cargando: false,
              esSuperuser: false,
              estadoSesion: SessionState.INVALID
            }));
          }
        }
      } catch (error) {
        if (isMounted) {
          setEstado(prev => ({
            ...prev,
            cargando: false,
            estadoSesion: SessionState.INVALID
          }));
        }
      }
    };

    inicializarAuth();
    
    // Exponer para testing en desarrollo
    if (process.env.NODE_ENV === 'development') {
      if (typeof window !== 'undefined') {
        (window as any).AuthServiceDebug = {
          simulateExpiredToken: () => {
            localStorage.setItem('mava_token', 'expired_token_for_testing');
            localStorage.setItem('mava_token_expiry', (Date.now() - 10000).toString());
            window.location.reload();
          },
          getToken: () => AuthServiceUAU.getToken(),
          isAuthenticated: () => AuthServiceUAU.isAuthenticated(),
          clearStorage: () => AuthServiceUAU.clearStorageSilently()
        };
      }
    }
    
    return () => { isMounted = false; };
  }, []);

  const iniciarSesion = useCallback(async (credenciales: LoginRequest): Promise<void> => {
    try {
      setEstado(prev => ({ ...prev, cargando: true, estadoSesion: SessionState.CHECKING }));
      
      // Usar ApiClient con URL correcta
      const response = await ApiClient.post<any>('/auth/login', credenciales);
      const loginData = response.data || response;
      
      const userInfo = loginData.user_info;
      const uauUserData: UAUUserData = {
        user_id: userInfo.id,
        username: userInfo.username,
        email: userInfo.email,
        full_name: userInfo.nombre_completo,
        is_active: userInfo.es_activo,
        user_type: userInfo.tipo_usuario,
        organizations: [],
        permissions: userInfo.permisos_menu.reduce((acc: Record<string, boolean>, perm: string) => {
          acc[perm] = true;
          return acc;
        }, {})
      };

      const usuarioMapeado = mapUAUToMavaUser(uauUserData);
      usuarioMapeado.permisos_menu = userInfo.permisos_menu;
      
      AuthServiceUAU.setToken(loginData.access_token);
      localStorage.setItem('uau_login_data', JSON.stringify(loginData));
      localStorage.setItem('mava_user', JSON.stringify(usuarioMapeado));
      localStorage.setItem('mava_permissions', JSON.stringify(uauUserData.permissions));
      
      setEstado({
        usuario: usuarioMapeado,
        estaAutenticado: true,
        cargando: false,
        esAdmin: usuarioMapeado.tipo_usuario === 'administrador',
        esSuperuser: userInfo.is_superuser || false,
        permisos: (usuarioMapeado.permisos || []).map((p: any) => p.codigo),
        idsMAquinas: usuarioMapeado.maquinas_asignadas?.map(ma => ma.maquina_id) || [],
        estadoSesion: SessionState.VALID,
        reconectando: false,
        organizaciones: uauUserData.organizations,
        empresaActiva: usuarioMapeado.empresa_activa
      });

      toast.success(`¡Bienvenido ${usuarioMapeado.nombre_completo}!`);
      
    } catch (error: any) {
      setEstado(prev => ({ 
        ...prev, 
        cargando: false,
        estadoSesion: SessionState.INVALID 
      }));
      
      if (error.response?.status === 401) {
        toast.error('Usuario o contraseña incorrectos');
      } else if (error.response?.status === 403) {
        toast.error('Cuenta bloqueada o sin permisos');
      } else if (error.response?.status === 422) {
        toast.error('Datos de acceso inválidos');
      } else {
        toast.error(error.message || 'Error al iniciar sesión');
      }
      
      throw error;
    }
  }, []);

  const cerrarSesion = useCallback(async (): Promise<void> => {
    try {
      const token = AuthServiceUAU.getToken();
      if (token) {
        // Intentar logout en servidor
        try {
          await ApiClient.post('/auth/logout');
        } catch (error) {
          // Continuar con limpieza local aunque falle el logout en servidor
        }
      }

      AuthServiceUAU.clearStorageSilently();
      
      setEstado({
        usuario: null,
        estaAutenticado: false,
        cargando: false,
        esAdmin: false,
        esSuperuser: false,
        permisos: [],
        idsMAquinas: [],
        estadoSesion: SessionState.INVALID,
        reconectando: false,
        organizaciones: [],
        empresaActiva: null
      });

      toast.success('Sesión cerrada correctamente');
      setTimeout(() => { window.location.href = '/login'; }, 100);
      
    } catch (error) {
      AuthServiceUAU.clearStorageSilently();
      window.location.href = '/login';
    }
  }, []);

  const tienePermiso = useCallback((permiso: string): boolean => {
    return estado.permisos.includes(permiso);
  }, [estado.permisos]);

  const tieneAlgunPermiso = useCallback((permisos: string[]): boolean => {
    return permisos.some(permiso => estado.permisos.includes(permiso));
  }, [estado.permisos]);

  const tieneTodosLosPermisos = useCallback((permisos: string[]): boolean => {
    return permisos.every(permiso => estado.permisos.includes(permiso));
  }, [estado.permisos]);

  const tieneAccesoMaquina = useCallback((maquinaId: number): boolean => {
    return estado.idsMAquinas.includes(maquinaId);
  }, [estado.idsMAquinas]);

  // Funciones stub para compatibilidad
  const actualizarPerfil = useCallback(async (): Promise<void> => {}, []);
  const editarPerfil = useCallback(async (_data: Partial<User>): Promise<void> => {}, []);
  const cambiarPassword = useCallback(async (_data: CambiarPasswordRequest): Promise<void> => {}, []);
  const verificarSesion = useCallback(async (): Promise<void> => {}, []);
  const manejarReconexion = useCallback(async (): Promise<void> => {}, []);
  const cambiarEmpresa = useCallback(async (_empresaId: number): Promise<void> => {}, []);
  const refrescarPermisos = useCallback(async (): Promise<void> => {}, []);

  const contextValue: AuthContextType = {
    ...estado,
    iniciarSesion,
    cerrarSesion,
    actualizarPerfil,
    editarPerfil,
    cambiarPassword,
    verificarSesion,
    manejarReconexion,
    cambiarEmpresa,
    refrescarPermisos,
    tienePermiso,
    tieneAlgunPermiso,
    tieneTodosLosPermisos,
    tieneAccesoMaquina,
    user: estado.usuario,
    isAuthenticated: estado.estaAutenticado,
    isLoading: estado.cargando,
    isAdmin: estado.esAdmin,
    isSuperuser: estado.esSuperuser,
    permissions: estado.permisos,
    sessionState: estado.estadoSesion,
    isReconnecting: estado.reconectando,
    hasPermission: tienePermiso,
    hasAnyPermission: tieneAlgunPermiso,
    hasAllPermissions: tieneTodosLosPermisos,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export default AuthProvider;
